import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Select, Spin, Switch, Card } from 'antd';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import { customBaseUrl, httpGet, httpPost } from '../../../store/http';
import { hideLoader, showLoader } from '../../../utils/loader';

const { Option } = Select;

const SingleCardRequest = ({ showModal, hideModal, getCards }) => {
  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('email'); // 'email', 'phoneNumber', 'userId'
  const [userData, setUserData] = useState(null);
  const [userAccounts, setUserAccounts] = useState([]);
  const [cardProducts, setCardProducts] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchingUser, setSearchingUser] = useState(false);

  // Fetch Card Products
  const fetchCardProducts = async () => {
    try {
      const response = await httpGet(`${customBaseUrl.cardUrl}/cms/cards/list/card-product`);
      if (response?.status) {
        setCardProducts(response.data);
      } else {
        NotificationManager.error(response?.message || 'Failed to fetch card products.');
      }
    } catch (error) {
      NotificationManager.error('Error fetching card products.');
      console.error('Error fetching card products:', error);
    }
  };

  // Fetch Card Types
  const fetchCardTypes = async () => {
    try {
      const response = await httpGet(`${customBaseUrl.cardUrl}/cms/cards/list/card-type`);
      if (response?.status) {
        setCardTypes(response.data);
      } else {
        NotificationManager.error(response?.message || 'Failed to fetch card types.');
      }
    } catch (error) {
      NotificationManager.error('Error fetching card types.');
      console.error('Error fetching card types:', error);
    }
  };

  useEffect(() => {
    fetchCardProducts();
    fetchCardTypes();
  }, []);

  const handleSearchUser = async () => {
    if (!searchValue) {
      NotificationManager.warning(`Please enter a user ${searchType}.`);
      return;
    }
    setSearchingUser(true);
    setUserData(null);
    setUserAccounts([]);
    try {
      let userRes;
      if (searchType === 'email') {
        userRes = await httpGet(`${customBaseUrl.authUrl}/admin/users/email/${searchValue}`);
      } else if (searchType === 'phoneNumber') {
        userRes = await httpGet(`${customBaseUrl.authUrl}/admin/users/phone/${searchValue}`);
      } else if (searchType === 'userId') {
        userRes = await httpGet(`${customBaseUrl.authUrl}/admin/users/${searchValue}`);
      }

      if (userRes?.success && userRes?.data) {
        setUserData(userRes.data);
        // Fetch user accounts
        const accountsRes = await httpGet(`${customBaseUrl.authUrl}/api/v1/accounts/user/${userRes.data.id}/details`);
        if (accountsRes?.success && accountsRes?.data?.accounts) {
          setUserAccounts(accountsRes.data.accounts);
        } else {
          NotificationManager.warning(accountsRes?.message || 'Failed to fetch user accounts.');
        }
      } else {
        NotificationManager.error(userRes?.message || 'User not found.');
      }
    } catch (error) {
      NotificationManager.error('Error searching for user.');
      console.error('Error searching user:', error);
    } finally {
      setSearchingUser(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!userData) {
      NotificationManager.error('Please search and select a user first.');
      return;
    }

    showLoader();
    try {
      const payload = {
        accounts: [values.accountToBeDebited, values.accountToLink].filter(Boolean),
        cardProduct: values.cardProduct,
        cardType: values.cardType,
        chargeCustomer: values.chargeCustomer,
        deliveryAddress: {
          address: values.address,
          city: values.city,
          id: 0,
          mobileNumber: userData.phoneNumber,
          state: values.state,
        },
        deliveryMethod: values.deliveryMethod,
        reference: `CARD_REQ_${Date.now()}`,
      };

      const headers = {
        pin: values.transactionPin,
      };

      const response = await httpPost(`${customBaseUrl.cardUrl}/cms/admin/card-request-with-account`, payload, false, null, false, headers);

      if (response?.success || response?.status) {
        NotificationManager.success(response?.message || 'Card request submitted successfully!');
        form.resetFields();
        setUserData(null);
        setUserAccounts([]);
        hideModal();
        getCards(); // Refresh the card requests list
      } else {
        NotificationManager.error(response?.message || 'Failed to submit card request.');
      }
    } catch (error) {
      NotificationManager.error('An error occurred while submitting the card request.');
      console.error('Error submitting card request:', error);
    } finally {
      hideLoader();
    }
  };

  return (
    <Modal
      title="Request Card on Behalf of User"
      visible={showModal}
      onCancel={hideModal}
      footer={null}
      width={800}
    >
      <Spin spinning={loading || searchingUser}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Search User by" required>
            <Input.Group compact>
              <Select value={searchType} onChange={(value) => setSearchType(value)}>
                <Option value="email">Email</Option>
                <Option value="phoneNumber">Phone Number</Option>
                <Option value="userId">User ID</Option>
              </Select>
              <Input
                style={{ width: 'calc(100% - 200px)' }}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={`Enter user ${searchType === 'email' ? 'email' : searchType === 'phoneNumber' ? 'phone number' : 'ID'}`}
              />
              <Button type="primary" onClick={handleSearchUser} loading={searchingUser}>
                Search
              </Button>
            </Input.Group>
          </Form.Item>

          {userData && (
            <Card title="User Details" style={{ marginBottom: 20 }}>
              <p><strong>Name:</strong> {userData.fullName}</p>
              <p><strong>Phone:</strong> {userData.phoneNumber}</p>
              <p><strong>User ID:</strong> {userData.id}</p>
            </Card>
          )}

          {userData && (
            <>
              <Form.Item name="accountToBeDebited" label="Account to be Debited" rules={[{ required: true, message: 'Please select an account!' }]}>
                <Select placeholder="Select account to be debited">
                  {userAccounts.map(account => (
                    <Option key={account.accountNumber} value={account.accountNumber}>
                      {account.accountName} ({account.accountNumber}) - {account.accountType}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="accountToLink" label="Account to Link (Optional)">
                <Select placeholder="Select account to link (if different from debited)">
                  {userAccounts.map(account => (
                    <Option key={account.accountNumber} value={account.accountNumber}>
                      {account.accountName} ({account.accountNumber}) - {account.accountType}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="cardProduct" label="Card Product" rules={[{ required: true, message: 'Please select a card product!' }]}>
                <Select placeholder="Select a card product">
                  {cardProducts.map(product => (
                    <Option key={product.id} value={product.productName}>
                      {product.productName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="cardType" label="Card Type" rules={[{ required: true, message: 'Please select a card type!' }]}>
                <Select placeholder="Select a card type">
                  {cardTypes.map(type => (
                    <Option key={type.id} value={type.type}>
                      {type.type}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="deliveryMethod" label="Delivery Method" rules={[{ required: true, message: 'Please select a delivery method!' }]}>
                <Select placeholder="Select delivery method">
                  <Option value="HOME_DELIVERY">Home Delivery</Option>
                  <Option value="BRANCH_PICKUP">Branch Pickup</Option>
                </Select>
              </Form.Item>

              <Form.Item name="address" label="Address" rules={[{ required: true, message: 'Please enter delivery address!' }]}>
                <Input placeholder="Street Address" />
              </Form.Item>
              <Form.Item name="city" label="City" rules={[{ required: true, message: 'Please enter city!' }]}>
                <Input placeholder="City" />
              </Form.Item>
              <Form.Item name="state" label="State" rules={[{ required: true, message: 'Please enter state!' }]}>
                <Input placeholder="State" />
              </Form.Item>

              <Form.Item name="chargeCustomer" label="Charge Customer" valuePropName="checked">
                <Switch defaultChecked />
              </Form.Item>

              <Form.Item name="transactionPin" label="Transaction PIN" rules={[{ required: true, message: 'Please enter transaction PIN!' }]}>
                <Input.Password placeholder="Enter transaction PIN" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" block>
                  Submit Card Request
                </Button>
              </Form.Item>
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default SingleCardRequest;
